<script setup>
    defineProps({
        manual: {
            required: true
        },
        isFullVersion: {
            type: Boolean,
            default: true
        }
    });

    const biologyHeaders = {
		temperature: 'Температурный режим',
		ground: 'Почва',
		light: 'Освещенность',
	}

	const seedlingHeaders = {
		ground: 'Грунт для посева',
		light: 'Освещение',
		seeding: 'Посев семян',
		hardening: 'Закаливание',
	}
</script>

<template>
<div class="bg-white sm:rounded-xl sm:shadow p-4 grid md:flex flex-wrap" v-if="manual.content">
    <div v-if="isFullVersion">
        <figure class="m-0 flex gap-4 flex-row-reverse" v-if="manual.content">		
            <img v-if="manual.image" class="xl:w-[300px] flex-shrink-0 self-start hidden lg:block bg-white border-none" :src="`/files/uploaded/${manual.image}.webp`" :alt="manual.category.title" />
            <figcaption class="flex-1">
                <h1 class="mt-0 text-3xl" v-if="manual.category">{{ manual.category.title }} - описание и условия выращивания</h1>
                <div class="text-black">
                    {{ manual.content.text }}
                </div>
            </figcaption>
        </figure>
    </div>

    <section class="lg:flex gap-8 items-start" id="seedling">
        <section v-if="Object.values(manual.biology).filter(it => it).length">
            <h2 class="text-2xl">Биология растения</h2>
            <dl class="grid gap-2 grid grid-cols-2 sm:grid-cols-3">
                <template v-for="(value, key) in manual.biology" :key="key">
                    <template v-if="value">
                        <div>
                        <h3>{{ biologyHeaders[key] }}</h3>
                        {{ value }}
                        </div>
                    </template>
                </template>
            </dl>
        </section>
    </section>
    
    <section class="lg:flex gap-8 items-start mb-8" id="seedling">
        <div>
            <h2 class="text-2xl">Выращивание рассады</h2>
            <template v-for="(value, key) in manual.seedling" :key="key">
                <template v-if="value">
                    <h3>{{ seedlingHeaders[key] }}</h3>
                    {{ value }}
                </template>
            </template>
        </div>

        <div class="flex flex-col gap-4 w-full lg:w-[320px] min-w-0 flex-shrink-0 mt-4" v-if="manual.good || manual.bad">
            <section class="ml-auto px-0 w-full rounded-xl">
				<AdBanner320 id="yandex_rtb_R-A-20123375-4-manual-site" />
			</section>
            <div class="flex flex-col items-start shadow-sm bg-slate-100 rounded-xl p-6 mb-4" v-if="manual.good">
                <div class="flex gap-2 m-0 mb-2 text-lg align-middle">
                <svg-icon name="line-chevron-down" width="25" height="25"/>
                Хорошие предшественники</div>
                <div class="grid grid-cols-2 w-full">
                <span class="" v-for="{slug, title} in manual.good">{{ title }}</span>
                </div>
            </div>
            
            <div class="flex flex-col items-start shadow-sm bg-slate-100 rounded-xl p-6 mb-4" v-if="manual.bad">
                <div class="flex gap-2 m-0 mb-2 text-lg align-middle">
                <svg-icon name="line-close-circle" width="25" height="25"/>
                Плохие предшественники</div>
                <div class="grid grid-cols-2 w-full">
                <span class="" v-for="{slug, title} in manual.bad">{{ title }}</span>
                </div>
            </div>
        </div>
    </section>

    <section class="grid grid-cols-2 sm:grid-cols-4 w-full mb-8" v-if="isFullVersion">
        <figure class="m-0">
            <svg-icon name="thermometer" width="70" height="70"/>
            <figcaption>
                Температура <br>
                грунта {{ manual.content.temp.ground.join('-') }} °C <br>
                воздуха {{ manual.content.temp.atmosphere.join('-') }} °C
            </figcaption>
        </figure>

        <figure class="m-0">
            <svg-icon name="seeding" width="70" height="70"/>
            <figcaption>
                Глубина посева <br> {{ manual.content.seeding_depth.join('-') }} см
            </figcaption>
        </figure>

        <figure class="m-0">
            <svg-icon name="sprout1" width="70" height="70"/>
            <figcaption>
                Всходы <br> {{ manual.content.germination.join('-')  }} дней
            </figcaption>
        </figure>

        <figure class="m-0">
            <svg-icon name="sprout" width="70" height="70"/>
            <figcaption>
                Возраст рассады <br> {{ manual.content.age.join('-')  }} дней
            </figcaption>
        </figure>
    </section>


    <section class="lg:flex gap-8 items-start mb-8">
        <div class="flex flex-col gap-4 xl:w-[300px] flex-shrink-0" v-if="manual.content.seeding_scheme || manual.content.watering">
            
			<div class="flex flex-col items-start shadow-sm bg-slate-100 rounded-xl p-6 mb-4" v-if="manual.content.seeding_scheme">
			<div v-if="manual.content.seeding_scheme">
                <div class="flex gap-2 m-0 mb-2 text-lg align-middle">
                <svg-icon name="line-md--grid-3-filled" width="25" height="25"/>
                Схема посадки</div>
                {{ manual.content.seeding_scheme }}
            </div>
            </div>
			<div class="flex flex-col items-start shadow-sm bg-slate-100 rounded-xl p-6 mb-4" v-if="manual.content.watering">
			<div v-if="manual.content.watering" id="watering">
                <div class="flex gap-2 m-0 mb-2 text-lg align-middle">
                <svg-icon name="line-md--paint-drop" width="25" height="25"/>
                Полив и влажность</div>
                {{ manual.content.watering }}
            </div>
			</div>
			
        </div>

        <div class="flex flex-col gap-8">
            <h2 class="m-0">Агротехника</h2>
            <div v-if="manual.content.seeding_features" id="seeding_features">
                <h3 class="mt-0">Высадка рассады (особенности)</h3>
                {{ manual.content.seeding_features }}
            </div>
            <div v-if="manual.content.care" id="care">
                <h3 class="mt-0">Уход и формирование растений</h3>
                {{ manual.content.care }}	
            </div>
            <div v-if="manual.content.fertilizers">
                <h3 class="mt-0">Удобрения и подкормки</h3>
                {{ manual.content.fertilizers }}	
            </div>
        </div>
    </section>
    
    <template v-if="isFullVersion">
        <section class="lg:flex gap-8 items-start lg:flex-row-reverse" v-if="manual.content.harvest || manual.content.summary || manual.content.description">
            <div class="flex flex-col gap-8 shadow-sm bg-slate-100 rounded-xl p-6 mb-4 xl:w-[300px] flex-shrink-0" v-if="manual.content.harvest">
                <div>
                    <div class="flex gap-2 m-0 mb-2 text-lg align-middle">
                <svg-icon name="arrow-close-down" width="25" height="25"/>
                Уборка урожая</div>
                    {{ manual.content.harvest }}
                </div>
            </div>

            <div class="flex flex-col gap-8" v-if="manual.content.summary || manual.content.description">
                <h2 class="m-0">Особенности выращивания</h2>
                <div v-if="manual.content.summary">
                    <h3 class="mt-0">Открытый грунт</h3>
                    {{ manual.content.summary }}
                </div>
                <div v-if="manual.content.description">
                    <h3 class="mt-0">Закрытый грунт</h3>
                    {{ manual.content.description }}	
                </div>
            </div>
        </section>


        <div class="bg-yellow-100 rounded-lg p-6" v-if="manual.content.tips">
            <h3 class="flex gap-2 m-0 mb-2 align-middle"><svg-icon name="sun-idea" width="25" height="25"/>
            Cоветы</h3>
            {{ manual.content.tips }}
        </div>
    </template>
</div>
</template>
